// Pure, wasp-free search + shared FAQ shapes. The public page fetches all
// published items once and filters here on every keystroke (dataset < 200).

export type FaqLocale = "fr" | "en";

export interface FaqItemPublic {
  id: string;
  slug: string;
  questionFr: string;
  answerFr: string;
  questionEn: string | null;
  answerEn: string | null;
  keywords: string | null;
  sortOrder: number;
}

export interface FaqCategoryPublic {
  id: string;
  slug: string;
  nameFr: string;
  nameEn: string;
  icon: string | null;
  sortOrder: number;
  items: FaqItemPublic[];
}

export interface FaqSearchResult {
  item: FaqItemPublic;
  categoryId: string;
  categoryName: string;
  question: string;
  answerPlain: string;
}

// Lowercase + strip diacritics so "Équity", "equity" and "equité" all match.
export function normalizeText(s: string): string {
  return (s ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");
}

// Locale-resolved question/answer for an item (EN falls back to FR when absent).
export function itemQuestion(item: FaqItemPublic, locale: FaqLocale): string {
  if (locale === "en" && item.questionEn) return item.questionEn;
  return item.questionFr;
}

export function itemAnswer(item: FaqItemPublic, locale: FaqLocale): string {
  if (locale === "en" && item.answerEn) return item.answerEn;
  return item.answerFr;
}

export function categoryName(cat: FaqCategoryPublic, locale: FaqLocale): string {
  return locale === "en" && cat.nameEn ? cat.nameEn : cat.nameFr;
}

// Flat list of results whose question + answer + keywords match the query.
export function filterFaq(
  categories: FaqCategoryPublic[],
  query: string,
  locale: FaqLocale,
): FaqSearchResult[] {
  const q = normalizeText(query).trim();
  if (!q) return [];
  const results: FaqSearchResult[] = [];
  for (const cat of categories) {
    const catName = categoryName(cat, locale);
    for (const item of cat.items) {
      const question = itemQuestion(item, locale);
      const answer = itemAnswer(item, locale);
      const haystack = normalizeText(`${question} ${answer} ${item.keywords ?? ""}`);
      if (haystack.includes(q)) {
        results.push({
          item,
          categoryId: cat.id,
          categoryName: catName,
          question,
          answerPlain: answer,
        });
      }
    }
  }
  return results;
}

// A short excerpt of `text` centered on the first occurrence of `query`.
export function buildSnippet(text: string, query: string, radius = 80): string {
  const norm = normalizeText(text);
  const idx = norm.indexOf(normalizeText(query));
  if (idx < 0) return text.slice(0, radius * 2).trim();
  const start = Math.max(0, idx - radius);
  const end = Math.min(text.length, idx + query.length + radius);
  return `${start > 0 ? "…" : ""}${text.slice(start, end).trim()}${end < text.length ? "…" : ""}`;
}
